/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { Table, Tooltip, Input, Alert, Spin } from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  SearchOutlined,
  HourglassOutlined,
  ExportOutlined,
  DeleteOutlined,
  TagOutlined,
} from '@ant-design/icons'
import { DollarOutlined } from '@ant-design/icons'
import CreditCardClockIcon from 'src/components/ts-components/icons/CreditCardClockIcon'
import RefundIcon from 'src/components/icons/RefundIcon'
import { Link } from 'react-router-dom'
import Text from 'src/components/ts-components/Text'
import {
  UserRegistration,
  useUserRegistrationData,
} from '@sportninja/common/hooks/useUserRegistrationData'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { formatShortDate } from '@sportninja/common/ts-utils/date-utils'
import { useRegistrationData } from '@sportninja/common/hooks/useRegistrationData'
import { formatCurrency, getErrorMessage } from '@sportninja/common/utils/utils'
import { useState, useMemo, useEffect, useCallback } from 'react'
import useWindowSize from 'src/hooks/useWindowSize'
import dayjs from 'dayjs'
import req from '@sportninja/common/api/request'
import DeleteSessionModal from './DeleteSessionModal'
import { resetRegistrationSession } from './api'
import { ROUTES } from '@sportninja/common/constants/app'
import { paragraphSmall, weightRegular } from 'src/components/css'
import { getTableStyles } from 'src/styles/tableStyles'
import { isCanlan } from '@sportninja/common/utils/customer-name'

const SCROLL_THRESHOLD = 500

const RegistrationList = () => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width < 1024, [width])
  const { types, fetchTypes } = useRegistrationData()
  const { registrationState, fetchUserRegistrations } =
    useUserRegistrationData()
  const { registrations, loading, error } = registrationState
  const [searchText, setSearchText] = useState('')
  const [openSessionError, setOpenSessionError] = useState<string | null>(null)

  // New state for delete session modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [sessionToDelete, setSessionToDelete] =
    useState<UserRegistration | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [visibleCount, setVisibleCount] = useState(20)

  useEffect(() => {
    if (!types || types === null) {
      fetchTypes()
    }
  }, [fetchTypes, types])

  const filteredRegistrations = useMemo(() => {
    if (!searchText) {
      return registrations
    }

    const searchLower = searchText.toLowerCase().trim()
    return registrations.filter((registration) => {
      const teamName = registration?.team?.name?.toLowerCase() || ''
      const scheduleName = registration?.schedule?.name?.toLowerCase() || ''

      return (
        teamName.includes(searchLower) || scheduleName.includes(searchLower)
      )
    })
  }, [registrations, searchText])

  // Reset visibleCount when search or registrations change
  useEffect(() => {
    setVisibleCount(20)
  }, [searchText, registrations])

  // Handle scroll for infinite loading
  const handleScroll = useCallback(() => {
    const { scrollTop, scrollHeight, clientHeight } = document.documentElement
    const nearBottom =
      scrollHeight - (scrollTop + clientHeight) < SCROLL_THRESHOLD
    if (nearBottom && visibleCount < filteredRegistrations.length && !loading) {
      setVisibleCount((prev) =>
        Math.min(prev + 20, filteredRegistrations.length)
      )
    }
  }, [visibleCount, filteredRegistrations.length, loading])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  const columns = [
    {
      title: 'Name',
      dataIndex: ['team', 'name'],
      key: 'team_name',
      width: 180,
      align: 'left',
      sorter: (a, b) => {
        if (!a.team?.name) {
          return 1
        }
        if (!b.team?.name) {
          return -1
        }
        return a?.team?.name?.localeCompare(b?.team?.name)
      },
      render: (text, record: UserRegistration) => {
        const handleClick = async () => {
          if (
            record.status_id === 6 ||
            // @ts-ignore
            record?.type === 'registration_session'
          ) {
            try {
              setOpenSessionError(null)
              await req(`/registration/session/${record.uid}/resume`, {
                method: 'PUT',
              })
              window.open(
                `/registration/${record.schedule.uid}?registration_session_uid=${record.uid}`,
                '_blank'
              )
            } catch (e) {
              const erroMessage = getErrorMessage(e)
              setOpenSessionError(erroMessage)
            }
          } else if (record?.billing?.length > 0) {
            window.location.href = `/registration-details/${record.schedule.uid}/info?registration_id=${record.uid}`
          }
        }

        if (!text) {
          const isTeamRegistration = record?.registration_type === 'Team'
          return (
            <Tooltip title='Restore Session'>
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 8px;
                  margin-left: 8px;
                  cursor: pointer;
                  &:hover {
                    text-decoration: underline;
                  }
                `}
                onClick={handleClick}
              >
                <img
                  src={
                    isTeamRegistration
                      ? '/images/generic_placeholders/placeholder_team.png'
                      : '/images/generic_placeholders/placeholder_player.png'
                  }
                  alt='team logo'
                  css={css`
                    width: 32px;
                    height: 32px;
                    background: ${colors.WHITE};
                    border-radius: 4px;
                    padding: 2px;
                    object-fit: contain;
                    margin-top: 4px;
                    align-self: center;
                  `}
                  onError={(e) => {
                    const img = e.target as HTMLImageElement
                    img.onerror = null
                    img.src =
                      '/images/generic_placeholders/placeholder_team.png'
                  }}
                />
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.WHITE}
                >
                  {isTeamRegistration
                    ? 'New Team Registration'
                    : 'New Player Registration'}
                </Text>
              </div>
            </Tooltip>
          )
        }

        const isBucket = record?.team?.is_bucket
        const playerName = record?.player
          ? `${record.player.name_first} ${record.player.name_last}`
          : null

        const playerImage = getImageThumbnailId(record.player)
        const teamImage = getImageThumbnailId(record.team)

        return (
          <div
            onClick={handleClick}
            css={css`
              &:hover {
                text-decoration: underline;
              }
              display: flex;
              align-items: ${playerName ? 'flex-start' : 'center'};
              gap: 8px;
              margin-left: 8px;
              cursor: ${record.status_id === 6 ||
              // @ts-ignore
              record?.type === 'registration_session' ||
              record?.billing?.length > 0
                ? 'pointer'
                : 'default'};
              /* on mobile, width is 100% */
              @media (max-width: 768px) {
                width: 100%;
              }
              max-width: 200px;
            `}
          >
            <img
              src={
                isBucket
                  ? '/images/bucket_image.png'
                  : playerName
                  ? playerImage ||
                    '/images/generic_placeholders/placeholder_player.png'
                  : teamImage ||
                    '/images/generic_placeholders/placeholder_team.png'
              }
              alt='team logo'
              css={css`
                width: 32px;
                height: 32px;
                background: ${colors.WHITE};
                border-radius: ${isBucket ? '8px' : '4px'};
                padding: ${isBucket ? '0px' : '2px'};
                object-fit: contain;
                margin-top: ${playerName ? '4px' : '0'};
                align-self: center;
              `}
              onError={(e) => {
                const img = e.target as HTMLImageElement
                img.onerror = null
                if (isBucket) {
                  img.src = '/images/bucket_image.png'
                } else if (playerName) {
                  img.src =
                    '/images/generic_placeholders/placeholder_player.png'
                } else {
                  img.src = '/images/generic_placeholders/placeholder_team.png'
                }
              }}
            />
            <div>
              {playerName ? (
                <>
                  <Text
                    variant='paragraphSmall'
                    weight='medium'
                    color={colors.WHITE}
                    style={`
                      display: block;
                      white-space: nowrap;
                      overflow: hidden;
                      text-overflow: ellipsis;
                      max-width: 200px;
                    `}
                  >
                    {playerName}
                  </Text>
                  <Text
                    variant='paragraphXSmall'
                    weight='regular'
                    color={colors.NEUTRAL_50}
                    style={`
                      display: block;
                      white-space: nowrap;
                      overflow: hidden;
                      text-overflow: ellipsis;
                      max-width: 200px;
                    `}
                  >
                    {text}
                  </Text>
                </>
              ) : (
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.WHITE}
                  style={`
                    display: block;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    max-width: 200px;
                  `}
                >
                  {text}
                </Text>
              )}
            </div>
          </div>
        )
      },
    },
    {
      title: 'Type',
      dataIndex: 'registration_type',
      key: 'registration_type',
      width: 80,
      align: 'left',
      sorter: (a, b) => a.registration_type.localeCompare(b.registration_type),
      render: (text) => (
        <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
          {text}
        </Text>
      ),
    },
    {
      title: isCanlan ? 'Season' : 'Competition',
      dataIndex: ['schedule', 'name'],
      key: 'schedule.name',
      width: 180,
      align: 'left',
      filters: registrations
        .map((reg) => ({
          text: reg?.schedule?.name,
          value: reg?.schedule?.name,
        }))
        .filter((v, i, a) => a.findIndex((t) => t.value === v.value) === i),
      onFilter: (value, record) => record?.schedule?.name === value,
      render: (text, record) => (
        <div
          css={css`
            width: 230px;
            /* on mobile, width is 100% */
            @media (max-width: 768px) {
              width: 100%;
            }
          `}
        >
          <Text
            variant='paragraphSmall'
            weight='regular'
            color={colors.WHITE}
            style={`
              text-transform: uppercase;
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
              overflow: hidden;
              text-overflow: ellipsis;
            `}
          >
            <Link
              to={`${ROUTES.SCHEDULE_ROOT}/${record.schedule.uid}${
                record.team && record.team.uid
                  ? `?sn_team=${record.team.uid}`
                  : ''
              }`}
              css={css`
                &:hover {
                  text-decoration: underline;
                }
              `}
            >
              {text}
            </Link>
          </Text>
          <Text
            variant='paragraphXSmall'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            {formatShortDate(record.schedule.start_date)} -{' '}
            {formatShortDate(record.schedule.end_date)}
          </Text>
        </div>
      ),
    },
    {
      title: 'Reg.Date',
      key: 'created_at',
      width: 100,
      align: 'left',
      sorter: (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      render: (_, record) => (
        <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
          {formatShortDate(record.created_at, false)}
        </Text>
      ),
    },
    {
      title: 'Outstanding',
      key: 'outstanding',
      width: 120,
      align: 'right',
      render: (_, record) => {
        const billingCurrency = record?.billing?.[0]?.currency
        const currency = types?.currency?.find(
          (c) => Number(c.id) === billingCurrency
        )?.name
        if (record.is_comp) {
          return <span>{formatCurrency(0, currency)}</span>
        }
        const isPlayerRegistration = record?.registration_type !== 'Team'
        const total =
          record?.billing
            ?.filter((item) =>
              isPlayerRegistration
                ? item.user_type === 'player'
                : item.user_type === 'teamRep'
            )
            .reduce((acc, curr) => acc + curr.subtotal, 0) || 0
        const paid =
          record?.billing
            ?.filter((item) =>
              isPlayerRegistration
                ? item.user_type === 'player'
                : item.user_type === 'teamRep'
            )
            .filter((item) => item.paid)
            .reduce((acc, curr) => acc + curr.subtotal, 0) || 0
        return (
          <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
            {formatCurrency(total - paid, currency)}
          </Text>
        )
      },
    },
    {
      title: 'Paid',
      key: 'paid',
      width: 120,
      align: 'right',
      render: (_, record) => {
        if (record?.coupon) {
          console.log('coupon', record?.coupon)
        }
        const deferPayment = record?.defer_payment || false
        const isCoupon = record?.is_coupon || false
        const billingCurrency = record?.billing?.[0]?.currency
        const currency = types?.currency?.find(
          (c) => Number(c.id) === billingCurrency
        )?.name
        const isPlayerRegistration = record?.registration_type !== 'Team'
        if (record.is_comp) {
          return <span>--</span>
        }
        let totalPaid = 0
        record?.billing
          ?.filter((item) =>
            isPlayerRegistration
              ? item.user_type === 'player'
              : item.user_type === 'teamRep'
          )
          .forEach((payment) => {
            if (payment.paid) {
              totalPaid += payment.display_subtotal
            } else if (dayjs(payment.due_date).isBefore(dayjs())) {
              // hasPaymentDelayed = true
            }
          })
        // Refund indicator
        const applicableBilling =
          record?.billing?.filter((item) =>
            isPlayerRegistration
              ? item.user_type === 'player'
              : item.user_type === 'teamRep'
          ) || []
        const refundedItems = applicableBilling.filter(
          (item) => item.refunded || (item.charge && !!item.charge.refunded)
        )
        const hasRefund = refundedItems.length > 0
        const totalRefunded = refundedItems.reduce((acc, item) => {
          const byField = Number(item.refund_subtotal || 0)
          const byCharge = Number(item.charge?.refund_amount || 0)
          return acc + (byField > 0 ? byField : byCharge)
        }, 0)
        const refundReasons = refundedItems
          .map((r) => r.charge?.refund_reason)
          .filter(Boolean) as string[]
        const refundNotes = refundedItems
          .map((r) => r.charge?.refund_note)
          .filter(Boolean) as string[]
        const firstRefundId =
          refundedItems.find((r) => r.charge?.stripe_id)?.charge?.stripe_id ||
          null
        return (
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              justify-content: flex-end;
              .ant-tooltip {
                max-width: 500px !important;
              }
            `}
          >
            {hasRefund && (
              <Tooltip
                placement='top'
                title={
                  <div>
                    {!!firstRefundId && (
                      <div
                        css={css`
                          /* display: inline-flex; */
                          display: flex;
                          align-items: center;
                          gap: 6px;
                          margin-bottom: 4px;
                          width: 100%;
                        `}
                      >
                        <div
                          css={css`
                            display: flex;
                            flex-direction: column;
                          `}
                        >
                          <strong>Transaction ID:</strong>
                          <span>{firstRefundId}</span>
                        </div>
                      </div>
                    )}
                    <div>
                      <strong>Refunded:</strong>{' '}
                      {formatCurrency(totalRefunded || 0, currency)}
                    </div>
                    {refundedItems.length > 1 && (
                      <div>
                        <strong>Transactions:</strong> {refundedItems.length}
                      </div>
                    )}

                    {!!refundReasons.length && (
                      <div>
                        <strong>Reason:</strong> {refundReasons[0]}
                      </div>
                    )}
                    {!!refundNotes.length && (
                      <div>
                        <strong>Note:</strong> {refundNotes[0]}
                      </div>
                    )}
                  </div>
                }
              >
                <span
                  css={css`
                    display: inline-flex;
                    align-items: center;
                  `}
                >
                  <RefundIcon size={20} color={colors.INFO_200} />
                </span>
              </Tooltip>
            )}
            {deferPayment ? (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  marginTop: 6,
                }}
              >
                <Tooltip title='Deferred Payment' placement='top'>
                  <span>
                    <CreditCardClockIcon size={16} color={colors.PRIMARY} />
                  </span>
                </Tooltip>
              </span>
            ) : isCoupon ? (
              <Tooltip
                placement='top'
                title={
                  record?.coupon ? (
                    <div>
                      <div>
                        <strong>Coupon Code:</strong> {record.coupon.code}
                      </div>
                      <div>
                        <strong>Discount:</strong>{' '}
                        {record.coupon.discount_type === 'percentage'
                          ? `${record.coupon.discount}%`
                          : formatCurrency(
                              record.coupon.discount_amount,
                              currency
                            )}
                      </div>
                      <div>
                        <strong>Type:</strong>{' '}
                        {record.coupon.discount_type === 'percentage'
                          ? 'Percentage'
                          : 'Amount'}
                      </div>
                    </div>
                  ) : (
                    'Coupon'
                  )
                }
              >
                <TagOutlined
                  css={css`
                    font-size: 16px;
                    color: ${colors.PRIMARY};
                  `}
                />
              </Tooltip>
            ) : null}
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.WHITE}
            >
              {formatCurrency(totalPaid || 0, currency)}
            </Text>
          </div>
        )
      },
    },
    {
      title: 'Status',
      key: 'status_id',
      dataIndex: 'status_id',
      width: 50,
      align: 'left',
      filters: [
        { text: 'Submitted', value: 1 },
        { text: 'Pending', value: 2 },
        { text: 'Approved', value: 3 },
        { text: 'Approved w/ Conditions', value: 4 },
        { text: 'Cancelled', value: 5 },
        { text: 'In Progress', value: 6 },
      ],
      onFilter: (value, record) => record.status_id === value,
      render: (status_id) => {
        const statusMap = {
          1: {
            icon: <HourglassOutlined />,
            tooltip: 'Submitted',
            color: colors.INFO_200,
          },
          2: {
            icon: <ExclamationCircleOutlined />,
            tooltip: 'Pending',
            color: colors.WARNING_200,
          },
          3: {
            icon: <CheckCircleOutlined />,
            tooltip: 'Approved',
            color: colors.SUCCESS_200,
          },
          4: {
            icon: <CheckCircleOutlined />,
            tooltip: 'Appd. w/ Cond.',
            color: colors.WARNING_200,
          },
          5: {
            icon: <CloseCircleOutlined />,
            tooltip: 'Cancelled',
            color: colors.ERROR_200,
          },
          6: {
            icon: <HourglassOutlined />,
            tooltip: 'In Progress',
            color: colors.INFO_200,
          },
        }

        const status = statusMap[status_id]

        return (
          <div
            css={css`
              display: inline-flex;
              align-items: center;
              gap: 8px;
              padding: 8px 4px;
              border-radius: 4px;
              color: ${status?.color};
              background: transparent;
              justify-content: flex-start;
              white-space: nowrap;
              width: 130px;
              /* on mobile, width is 100% */
              @media (max-width: 768px) {
                width: 100%;
                justify-content: flex-start;
              }
            `}
          >
            <span
              css={css`
                font-size: 16px;
                display: flex;
                align-items: center;
                flex-shrink: 0;
              `}
            >
              {status?.icon}
            </span>
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.WHITE}
              style={`
                max-width: 100px;
                display: -webkit-box;
                -webkit-box-orient: vertical;
                -webkit-line-clamp: 2;
                overflow: hidden;
                text-overflow: ellipsis;
              `}
            >
              {status?.tooltip}
            </Text>
          </div>
        )
      },
    },
    {
      // title: (
      //   <Text
      //     variant='captionSmall'
      //     weight='semiBold'
      //     color={colors.NEUTRAL_0}
      //     style={`
      //       text-transform: uppercase;
      //       text-align: left;
      //     `}
      //   >
      //     <Tooltip title='Options' placement='top' zIndex={11000}>
      //       <MenuOutlined
      //         css={css`
      //           font-size: 16px;
      //           color: ${colors.NEUTRAL_100};
      //         `}
      //       />
      //     </Tooltip>
      //   </Text>
      // ),
      title: '',
      key: 'actions',
      width: 50,
      align: 'left',
      render: (_, record) => {
        const isSession =
          record.status_id === 6 || record.type === 'registration_session'
        const isPlayerRegistration = record?.registration_type !== 'Team'
        const applicableBilling =
          record?.billing?.filter((item) =>
            isPlayerRegistration
              ? item.user_type === 'player'
              : item.user_type === 'teamRep'
          ) || []
        const pendingPayments = applicableBilling.filter((b) => !b.paid)
        const hasPending = pendingPayments.length > 0

        return (
          <div
            css={css`
              display: flex;
              justify-content: left;
              align-items: center;
              gap: 8px;
            `}
          >
            <Tooltip title={isSession ? 'Restore Session' : 'View Details'}>
              {isSession ? (
                <ExportOutlined
                  css={css`
                    font-size: 20px;
                    color: ${colors.PRIMARY};
                    cursor: pointer;
                  `}
                  onClick={async () => {
                    try {
                      setOpenSessionError(null)
                      await req(`/registration/session/${record.uid}/resume`, {
                        method: 'PUT',
                      })
                      window.open(
                        `/registration/${record.schedule.uid}?registration_session_uid=${record.uid}`,
                        '_blank'
                      )
                    } catch (e) {
                      const erroMessage = getErrorMessage(e)
                      setOpenSessionError(erroMessage)
                    }
                  }}
                />
              ) : (
                <Link
                  to={`/registration-details/${record.schedule.uid}/info?registration_id=${record.uid}`}
                  css={css`
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 4px;
                    cursor: pointer;
                  `}
                >
                  <RefundIcon size={24} color={colors.PRIMARY} />
                </Link>
              )}
            </Tooltip>
            {/* Pay button when there are pending payments */}
            {!isSession && hasPending && (
              <div
                css={css`
                  display: inline-flex;
                  align-items: center;
                  gap: 4px;
                  cursor: pointer;
                  border: 1px solid ${colors.PRIMARY};
                  padding: 2px 8px;
                  border-radius: 8px;
                  background-color: ${colors.PRIMARY};
                  height: 28px;
                `}
                onClick={() => {
                  if (pendingPayments.length === 1) {
                    const billingId = pendingPayments[0].uid
                    const teamId = record?.team?.uid
                    const rosterId = record?.team?.team_player_roster_id
                    const url = `${ROUTES.REGISTRATION_PAYMENT}?teamId=${teamId}&rosterId=${rosterId}&billingId=${billingId}`
                    window.open(url, '_blank')
                    return
                  }

                  // More than one pending payment: go to Billing tab
                  window.location.href = `/registration-details/${record.schedule.uid}/billing?registration_id=${record.uid}`
                }}
              >
                <DollarOutlined
                  css={css`
                    font-size: 18px;
                    color: ${colors.SECONDARY_300};
                  `}
                />
                {!isMobile && (
                  <span
                    css={css`
                      color: ${colors.SECONDARY_300};
                      font-size: 14px;
                      line-height: 20px;
                    `}
                  >
                    Pay
                  </span>
                )}
              </div>
            )}
            {isSession && (
              <Tooltip title='Delete Session'>
                <DeleteOutlined
                  css={css`
                    font-size: 20px;
                    color: ${colors.ERROR_200};
                    cursor: pointer;
                    &:hover {
                      opacity: 0.8;
                    }
                  `}
                  onClick={() => handleOpenDeleteSession(record)}
                />
              </Tooltip>
            )}
          </div>
        )
      },
    },
  ]

  // Add new handlers
  const handleOpenDeleteSession = (record: UserRegistration) => {
    setSessionToDelete(record)
    setIsDeleteModalOpen(true)
    setDeleteError(null)
  }

  const handleDeleteSessionConfirm = async () => {
    if (!sessionToDelete) {
      return
    }

    try {
      setIsDeleting(true)
      setDeleteError(null)

      // Call the API to reset the session
      await resetRegistrationSession(sessionToDelete.uid)

      // Refresh the list
      await fetchUserRegistrations(true)

      setIsDeleteModalOpen(false)
      setSessionToDelete(null)
    } catch (e) {
      const errorMessage = getErrorMessage(e)
      setDeleteError(errorMessage || 'Failed to delete session')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false)
    setSessionToDelete(null)
    setDeleteError(null)
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        margin-top: 16px;
        flex: 1;
        width: 100%;
        ${isMobile &&
        css`
          padding-bottom: 48px;
        `}
      `}
    >
      {error ? (
        <Alert
          message={error}
          type='error'
          showIcon
          css={css`
            margin-bottom: 16px;
          `}
        />
      ) : (
        <>
          <div
            css={css`
              display: flex;
              justify-content: flex-start;
              margin-bottom: 16px;
              /* on mobile, width is 100% */
              @media (max-width: 768px) {
                width: 100%;
                padding: 0 16px;
              }
            `}
          >
            <Input
              placeholder='Search by Team or Competition'
              prefix={
                <SearchOutlined
                  css={css`
                    font-size: 16px;
                    color: ${colors.NEUTRAL_50};
                  `}
                />
              }
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              css={css`
                ${paragraphSmall}
                ${weightRegular}
                width: 300px;
                color: ${colors.WHITE};
                border-radius: 8px;
                background: transparent;
                height: 40px !important;
                .ant-input {
                  background-color: transparent;
                  color: ${colors.WHITE};
                }
                .ant-input-prefix {
                  color: ${colors.NEUTRAL_100};
                }
                &::placeholder {
                  color: ${colors.NEUTRAL_100};
                }
                @media (max-width: 768px) {
                  width: 90vw;
                }
                // remove input hover background
                &:hover {
                  background-color: transparent;
                }
              `}
            />
          </div>
          {openSessionError && (
            <Alert
              message={openSessionError}
              type='error'
              showIcon
              css={css`
                margin-bottom: 16px;
              `}
            />
          )}
          {loading && filteredRegistrations.length === 0 ? (
            <div
              css={css`
                display: flex;
                justify-content: center;
                align-items: center;
                min-height: 200px;
              `}
            >
              <Spin
                size='small'
                css={css`
                  .ant-spin-dot-item {
                    background-color: ${colors.PRIMARY};
                  }
                `}
              />
            </div>
          ) : (
            <Table
              // @ts-ignore
              columns={columns}
              dataSource={filteredRegistrations.slice(0, visibleCount)}
              size='small'
              scroll={{ x: isMobile || width < 1200 ? 1500 : undefined }}
              pagination={false}
              showSorterTooltip={false}
              css={getTableStyles(isMobile)}
            />
          )}
          {/* Infinite scroll footer */}
          {!loading &&
            visibleCount >= filteredRegistrations.length &&
            filteredRegistrations.length > 0 && (
              <div
                css={css`
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  padding: 24px 0;
                  min-height: 60px;
                `}
              >
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                >
                  No More Results
                </Text>
              </div>
            )}
        </>
      )}

      {/* Add DeleteSessionModal */}
      <DeleteSessionModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteModalClose}
        onConfirm={handleDeleteSessionConfirm}
        isLoading={isDeleting}
        error={deleteError}
      />
    </div>
  )
}

export default RegistrationList
